import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import "./Sidebar.css";
import useIsMobile from "../../hooks/useIsMobile";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "../../hooks/useRouter";
import Modal from "../../components/Modal/Modal";
import GenericTooltip from "../GenericTooltip/GenericTooltip";
import Policy from "../Policy/Policy";
import Threads from "../Thread/Thread";
import { RBAC_APPS } from "../../constant/rbacConfig";
import { ROUTES, STORAGE_KEYS, AGENT_TYPES, ROLE_ACCESS } from "../../constant/keyConstants";
import { DEBUG_MODE_ENABLED, RBAC_ENABLED } from "../../constant/globalConstants";

import useOutsideClick from "../../hooks/useOutsideClick";
import { useAuth } from "../../hooks/useAuth";
import { useRbac } from "../../hooks/useRbac";
import { getInitials } from "../../utils/commonHelper";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { useConstants } from "../../constant/constantProvider";
import { useTour } from "../../contexts/TourProvider";
import { useNewChat } from "../../hooks/useNewChat";

import {
  acceptMainPolicy,
  acceptDOCPolicy,
  getUserThreadsConversation,
  updateUserThreadRequest,
  getUserGMTThreadsConversation,
  deleteGMTUserThreadRequest,
  renameGMTUserThreadRequest,
  getUserProcurementThreadsConversation,
  deleteProcurementUserThreadRequest,
  renameProcurementUserThreadRequest,
  getDeepInsightsThreadsConversation,
  updateDeepInsightsSessionRequest,
  getTalentPulseThreadsConversation,
  updateTalentPulseUserThread,
  triggerLogout
} from "../../store/apiActions";

import { setSidebarCollapsed } from "../../store/actions";

// assets
import aiUsagePolicyPDF from "../../assets/documents/UPL AI Acceptable Usage Policy.pdf";
import modelSelectionGuidePDF from "../../assets/documents/UPL Genie Model Selection Guide - Mar 20th.pdf";
import chatGenieUserGuidePDF from "../../assets/documents/UPL Genie User Guide-20 Mar .pdf";
import faqPDF from "../../assets/documents/UPL Genie FAQ - 19th feb - without image generation.pdf";

import logoutIcon from "../../assets/new-images/logout-icon.svg";
import expandSidebarIcon from "../../assets/new-images/sidebar-collapse.svg";
import collapseSidebarIcon from "../../assets/new-images/sidebar-expand.svg";
import blueCloseIcon from "../../assets/new-images/close-icon.svg";
import newChatIcon from "../../assets/new-images/new-chat-icon.svg";
import historyIcon from "../../assets/new-images/history-icon.svg";
import historyActiveIcon from "../../assets/new-images/history-active-icon.svg";
import appsIcon from "../../assets/new-images/apps-icon.svg";
import appsActiveIcon from "../../assets/new-images/apps-active-icon.svg";
import adminIcon from "../../assets/new-images/admin-icon.svg";
import adminActiveIcon from "../../assets/new-images/active-admin-icon.svg";
import pulseSurveyIcon from "../../assets/new-images/pulse-survey-icon.svg";
import pulseSurveyActiveIcon from "../../assets/new-images/pulse-survey-active-icon.svg";
import projectIcon from "../../assets/new-images/project-icon.svg";
import newsIcon from "../../assets/new-images/news-icon.svg";
import aboutIcon from "../../assets/new-images/model-selection-icon.svg";
import policyIcon from "../../assets/new-images/ai-usage-icon.svg";
import faqIcon from "../../assets/new-images/faq-icon.svg";
import feedbackIcon from "../../assets/new-images/feedback-icon.svg";
import userGuideIcon from "../../assets/new-images/user-guide-icon.svg";
import tourGuideIcon from "../../assets/new-images/interface-tour-icon.svg";
import videoTutorial from "../../assets/new-images/video-tutorials-icon.svg";
import libraryIcon from "../../assets/new-images/prompt-library-icon.svg";
import playbooksIcon from "../../assets/new-images/playbooks-icon.svg";
import dataConnectorIcon from "../../assets/new-images/data-connector-icon.svg";
import UPL_GENIE_LOGO from "../../assets/images/upl-genie-logo.svg";
import GMT_LOGO from "../../assets/new-images/gmt-logo.svg";
import DEEP_INSIGHT_LOGO from "../../assets/new-images/deep-insights-logo-icon.svg";
import TALENT_PULSE_360_LOGO from "../../assets/icons/app-icons/talent-pulse-360-icon.svg";

function Sidebar() {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const isCollapsed = useSelector((state) => state.state?.sidebarCollapsed ?? true);
  const setIsCollapsed = useCallback((val) => dispatch(setSidebarCollapsed(val)), [dispatch]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const sidebarRef = useRef(null);
  const profileRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownHeight, setDropdownHeight] = useState(300);
  useOutsideClick(profileRef, () => setIsProfileMenuOpen(false));

  const navigate = useNavigate();
  const location = useLocation();
  const { emit, subscribe } = useEventEmitter();
  const { selectedAgent } = useSelector((state) => state.state || {});

  const { username, fullName, ugdnId: userUGDN } = useAuth();
  const { getUserRole, getAppData, rbacData } = useRbac();

  const isTalentPulseAdmin = useMemo(() => {
    if (DEBUG_MODE_ENABLED || !RBAC_ENABLED) return true;
    if (!rbacData) return false;
    const normalizeRole = (r) => (r ? r.toUpperCase().replace(/\s+/g, "") : "");
    const appData = getAppData(RBAC_APPS.TALENT_PULSE_360);
    const appRole = getUserRole(RBAC_APPS.TALENT_PULSE_360);
    const n = normalizeRole(ROLE_ACCESS.SUPERADMIN);
    return n === normalizeRole(appRole) || n === normalizeRole(appData?.role_name);
  }, [getAppData, getUserRole, rbacData]);

  const initials = getInitials(fullName);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { handleNewChat: handleConfirmNewChat } = useNewChat();

  // Policy Modal States from Dev Branch
  const [modelTitle, setModelTitle] = useState("");
  const [openPDF, setOpenPDF] = useState(false);
  const [isPersonalPolicyAccepted, setIsPersonalPolicyAccepted] = useState(false);

  const {
    LOGOUT, MY_APPS,
    AI_USAGE_POLICY, FAQS, USER_GUIDE_DOC,
    VIDEO_TUTORIALS, USER_GUIDE, FEEDBACK, MODEL_SELECTION_GUIDE,
    PII_POLICY, LOGOUT_CONFIRM_MSG, CANCEL,
    NEW_CHAT, TOUR_GUIDE
  } = useConstants();

  const [showUserGuideSubMenu, setShowUserGuideSubMenu] = useState(false);
  const { startTour } = useTour();
  const sidebarTabs = useSelector((state) => state.menu.sidebarTabs);
  const [manuallyCollapsedHistory, setManuallyCollapsedHistory] = useState(false);
  const isHistoryCollapsed = manuallyCollapsedHistory;

  const iconMap = useMemo(() => ({
    newChatIcon,
    historyIcon,
    historyActiveIcon,
    appsIcon,
    appsActiveIcon,
    projectIcon,
    newsIcon,
    libraryIcon,
    playbooksIcon,
    adminIcon,
    adminActiveIcon,
    pulseSurveyIcon,
    pulseSurveyActiveIcon,
    dataConnectorIcon,
  }), []);

  useEffect(() => {
    const unsubscribe = subscribe("closeSidebar", () => {
      setIsCollapsed(true);
    });
    return () => unsubscribe();
  }, [subscribe, setIsCollapsed]);

  const handleOpenModal = useCallback((policyType) => {
    setIsProfileMenuOpen(false);
    setModelTitle(policyType || MODEL_SELECTION_GUIDE);
    setOpenPDF(true);
    emit("triggerPolicyModal", policyType);
  }, [emit, MODEL_SELECTION_GUIDE]);

  const handlePolicyAccept = () => {
    setOpenPDF(false);
    emit("policyPopupClosed");

    if (modelTitle === PII_POLICY && !isPersonalPolicyAccepted) {
      setIsPersonalPolicyAccepted(true);
      localStorage.setItem(STORAGE_KEYS.IS_PERSONAL_POLICY, true);
      dispatch(acceptDOCPolicy({ ugdn_id: userUGDN }));
    } else if (modelTitle === AI_USAGE_POLICY && localStorage.getItem(STORAGE_KEYS.IS_POLICY) === "false") {
      localStorage.setItem(STORAGE_KEYS.IS_POLICY, true);
      dispatch(acceptMainPolicy({ ugdn_id: username }));
    }
  };

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEYS.IS_POLICY) === "false") {
      handleOpenModal(AI_USAGE_POLICY);
    }
  }, [AI_USAGE_POLICY, handleOpenModal]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = localStorage.getItem(STORAGE_KEYS.IS_PERSONAL_POLICY);
      if (data) {
        setIsPersonalPolicyAccepted(String(data).toLowerCase() === "true");
        clearInterval(interval);
      }
    }, 400);
    return () => clearInterval(interval);
  }, []);

  const labelsMap = useMemo(() => ({
    FEEDBACK,
    FAQS,
    USER_GUIDE,
    VIDEO_TUTORIALS,
    USER_GUIDE_DOC,
    MODEL_SELECTION_GUIDE,
    AI_USAGE_POLICY,
    LOGOUT,
    TOUR_GUIDE
  }), [FEEDBACK, FAQS, USER_GUIDE, VIDEO_TUTORIALS, USER_GUIDE_DOC, MODEL_SELECTION_GUIDE, AI_USAGE_POLICY, LOGOUT, TOUR_GUIDE]);

  const profileMenuItemsConfig = useSelector((state) => state.menu.profileMenuItems);

  const profileMenuItems = useMemo(() => {
    const actionMap = {
      openFeedback: () => { setIsProfileMenuOpen(false); window.open("https://forms.office.com/r/i2Wzj1baFs", "_blank"); },
      openFAQ: () => { setIsProfileMenuOpen(false); handleOpenModal(FAQS); },
      openVideoTutorials: () => {
        setIsProfileMenuOpen(false);
        setShowUserGuideSubMenu(false);
        navigate("/video-tutorials");
        if (isMobile) emit("closeSidebar");
      },
      openUserGuideDoc: () => { setIsProfileMenuOpen(false); setShowUserGuideSubMenu(false); handleOpenModal(USER_GUIDE_DOC); },
      openModelGuide: () => handleOpenModal(MODEL_SELECTION_GUIDE),
      openPolicy: () => handleOpenModal(AI_USAGE_POLICY),
      openLogout: () => { setIsProfileMenuOpen(false); setShowLogoutModal(true); },
      startTour,
    };

    const iconResourceMap = {
      feedbackIcon,
      faqIcon,
      userGuideIcon,
      videoTutorial,
      aboutIcon,
      policyIcon,
      logoutIcon,
      tourGuideIcon
    };

    const items = profileMenuItemsConfig.map(item => {
      const mappedItem = {
        id: item.id,
        label: labelsMap[item.labelKey] || item.labelKey,
        icon: iconResourceMap[item.icon] || item.icon,
        action: actionMap[item.action]
      };

      if (item.hasSubmenu && item.submenuItems) {
        mappedItem.hasSubmenu = true;
        mappedItem.submenuItems = item.submenuItems.map(subItem => ({
          id: subItem.id,
          label: labelsMap[subItem.labelKey] || subItem.labelKey,
          icon: iconResourceMap[subItem.icon] || subItem.icon,
          action: actionMap[subItem.action]
        }));
      }

      return mappedItem;
    });

    const isChatPath = location.pathname.startsWith("/chat") || location.pathname === "/video-tutorials";
    const isDeepInsightsPath = location.pathname.startsWith("/deep-insights");

    if (!isChatPath) {
      if (isDeepInsightsPath || selectedAgent === RBAC_APPS.DEEP_INSIGHTS) {
        return items.filter(item => item.id === 'logout');
      }
      return items.filter(item => !['feedback', 'faq', 'user-guide', 'model-guide'].includes(item.id));
    }

    return items;
  }, [
    profileMenuItemsConfig, labelsMap,
    navigate, handleOpenModal, setIsProfileMenuOpen, setShowUserGuideSubMenu,
    location.pathname, selectedAgent, emit, isMobile, FAQS, USER_GUIDE_DOC, MODEL_SELECTION_GUIDE, AI_USAGE_POLICY, startTour
  ]);

  useEffect(() => {
    if (isProfileMenuOpen && dropdownRef.current) {
      setDropdownHeight(dropdownRef.current.offsetHeight + 15);
    }
  }, [isProfileMenuOpen, profileMenuItems]);

  const threadConfig = useMemo(() => ({
    "new-chat": {
      getThreadsAction: getUserThreadsConversation,
      deleteThreadAction: updateUserThreadRequest,
      renameThreadAction: updateUserThreadRequest,
      pinThreadAction: updateUserThreadRequest,
      sessionStorageKey: "sessionId",
      agentType: AGENT_TYPES.CHAT_GENIE,
      reduxStateKey: "getUserThreads",
      threadFieldNames: { sessionId: "session_id", threadTitle: "thread_title" },
    },
    "gmt-chat": {
      getThreadsAction: getUserGMTThreadsConversation,
      deleteThreadAction: deleteGMTUserThreadRequest,
      renameThreadAction: renameGMTUserThreadRequest,
      sessionStorageKey: "GMTSessionId",
      agentType: AGENT_TYPES.GMT,
      reduxStateKey: "getGMTHistoryList",
      threadFieldNames: { sessionId: "session_id", threadTitle: "session_title" },
    },
    "proc-ai": {
      getThreadsAction: getUserProcurementThreadsConversation,
      deleteThreadAction: deleteProcurementUserThreadRequest,
      renameThreadAction: renameProcurementUserThreadRequest,
      sessionStorageKey: "procAISessionId",
      agentType: AGENT_TYPES.PROCUREMENT_AI,
      reduxStateKey: "getProcurementHistoryList",
      threadFieldNames: { sessionId: "session_id", threadTitle: "thread_title" },
    },
    "deep-insights-chat": {
      getThreadsAction: getDeepInsightsThreadsConversation,
      deleteThreadAction: updateDeepInsightsSessionRequest,
      renameThreadAction: updateDeepInsightsSessionRequest,
      pinThreadAction: updateDeepInsightsSessionRequest,
      sessionStorageKey: "deepInsightsSessionId",
      agentType: AGENT_TYPES.DEEP_INSIGHTS,
      reduxStateKey: "getDeepInsightsHistoryList",
      threadFieldNames: { sessionId: "session_id", threadTitle: "thread_title" },
    },
    "talent-pulse-360": {
      getThreadsAction: getTalentPulseThreadsConversation,
      deleteThreadAction: updateTalentPulseUserThread,
      renameThreadAction: updateTalentPulseUserThread,
      pinThreadAction: updateTalentPulseUserThread,
      sessionStorageKey: "talentPulse360SessionId",
      agentType: AGENT_TYPES.TALENT_PULSE_360,
      reduxStateKey: "getTalentPulseHistoryList",
      threadFieldNames: { sessionId: "session_id", threadTitle: "thread_title" },
    },
  }), []);

  const getActiveThreadConfig = useCallback(() => {
    if (location.pathname.startsWith("/chat")) return threadConfig["new-chat"];
    if (location.pathname.startsWith("/gmt-chat")) return threadConfig["gmt-chat"];
    if (location.pathname.startsWith("/proc-ai")) return threadConfig["proc-ai"];
    if (location.pathname.startsWith("/deep-insights-chat")) return threadConfig["deep-insights-chat"];
    if (location.pathname.startsWith("/talent-pulse-360")) return threadConfig["talent-pulse-360"];
    return threadConfig["new-chat"];
  }, [location.pathname, threadConfig]);


  const handleTabClick = useCallback((tab) => {
    if (isCollapsed) {
      if (tab.id !== "new-chat" && tab.id !== "apps" && tab.id !== "prompt-library") {
        setIsCollapsed(false);
      }
    }

    if ((tab.id === "new-chat" || tab.id === "apps") && isMobile) {
      setIsCollapsed(true);
    }

    if (tab.id === "new-chat") {
      handleConfirmNewChat();
    } else if (tab.id === "prompt-library") {
      emit("openPromptLibrary");
      if (isMobile) setIsCollapsed(true);
    } else if (tab.id === "connectors") {
      emit("openDeepInsightsConnectors");
      if (isMobile) setIsCollapsed(true);
    } else if (tab.id === "playbooks") {
      emit("openDeepInsightsPlaybooks");
      if (isMobile) setIsCollapsed(true);
    } else {
      navigate(tab.path);
    }
    // eslint-disable-next-line
  }, [isCollapsed, isMobile, handleConfirmNewChat, navigate, setIsCollapsed, selectedAgent, emit]);

  const handleLogout = useCallback(async () => {
    try {
      if (triggerLogout) {
        await dispatch(triggerLogout());
      }
    } catch (error) {
      console.error(error);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem("logout", true);
      setShowLogoutModal(false);
      navigate("/sign-in");
    }
  }, [dispatch, navigate]);

  useOutsideClick(sidebarRef, () => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  });

  const activeThreadConfig = getActiveThreadConfig();

  const showSearchBarInThreads =
    activeThreadConfig?.agentType === AGENT_TYPES.CHAT_GENIE ||
    activeThreadConfig?.agentType === AGENT_TYPES.PROCUREMENT_AI ||
    activeThreadConfig?.agentType === AGENT_TYPES.DEEP_INSIGHTS ||
    activeThreadConfig?.agentType === AGENT_TYPES.TALENT_PULSE_360;

  const showThreads = useMemo(() => {
    const pathname = location.pathname;
    const agentAllowsThreads =
      selectedAgent === RBAC_APPS.CHAT_GENIE ||
      selectedAgent === RBAC_APPS.PROCUREMENT ||
      selectedAgent === RBAC_APPS.GMT ||
      selectedAgent === RBAC_APPS.DEEP_INSIGHTS ||
      selectedAgent === RBAC_APPS.TALENT_PULSE_360;
    const pathAllowsThreads =
      pathname.startsWith(ROUTES.CHAT) ||
      pathname.startsWith(ROUTES.PROCUREMENT_AI) ||
      pathname.startsWith(ROUTES.GMT_CHAT) ||
      pathname.startsWith(ROUTES.DEEP_INSIGHTS_CHAT) ||
      pathname.startsWith(ROUTES.TALENT_PULSE_360);
    return (
      agentAllowsThreads &&
      pathAllowsThreads &&
      pathname !== ROUTES.MY_APPS &&
      pathname !== ROUTES.USER_MANAGEMENT &&
      pathname !== ROUTES.VIDEO_TUTORIALS &&
      pathname !== ROUTES.TALENT_PULSE_360_ADMIN &&
      pathname !== ROUTES.TALENT_PULSE_360_SURVEY
    );
  }, [selectedAgent, location.pathname]);

  return (
    <>
      <div
        ref={sidebarRef}
        className={`sidebar-container ${isCollapsed ? "collapsed" : "expanded"}`}
      >
        <div className="sidebar-header">
          <button
            className="collapse-toggle-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? "Expand sidebar" : ""}
          >
            <img
              src={
                isMobile && !isCollapsed
                  ? blueCloseIcon
                  : isCollapsed
                    ? expandSidebarIcon
                    : collapseSidebarIcon
              }
              alt="toggle-sidebar"
            />
          </button>

          {!isCollapsed && (
            <button className="button-style"
              onClick={() => {
                if (isMobile) emit("closeSidebar");
                handleConfirmNewChat();
              }}
              type="button"
            >
              <img
                src={
                  selectedAgent === RBAC_APPS.GMT
                    ? GMT_LOGO
                    : selectedAgent === RBAC_APPS.DEEP_INSIGHTS
                      ? DEEP_INSIGHT_LOGO
                      : selectedAgent === RBAC_APPS.TALENT_PULSE_360
                        ? TALENT_PULSE_360_LOGO
                        : UPL_GENIE_LOGO
                }
                alt="UPL Genie"
                className="sidebar-brand-logo"
              />
            </button>
          )}
        </div>

        <div className="sidebar-nav">
          <div className="sidebar-tabs">
            {sidebarTabs
              .filter((tab) => {
                const isTalentPulsePage =
                  location.pathname.startsWith(ROUTES.TALENT_PULSE_360) ||
                  location.pathname.startsWith(ROUTES.TALENT_PULSE_360_ADMIN) ||
                  location.pathname.startsWith(ROUTES.TALENT_PULSE_360_SURVEY) ||
                  selectedAgent === RBAC_APPS.TALENT_PULSE_360;
                if (tab.id === "admin") return !isMobile && isTalentPulsePage && isTalentPulseAdmin;
                if (tab.id === "pulse-survey" || tab.id === "talent-pulse-survey") return !isMobile && isTalentPulsePage && isTalentPulseAdmin && tab.enabled !== false;
                if (tab.id === "apps") return selectedAgent !== RBAC_APPS.GMT;
                if (tab.id === "prompt-library") return selectedAgent === RBAC_APPS.CHAT_GENIE && location.pathname.startsWith(ROUTES.CHAT);
                // Product-specific DeepInsights tools must not appear in other apps.
                if (tab.id === "alerts" || tab.id === "connectors" || tab.id === "playbooks") {
                  return selectedAgent === RBAC_APPS.DEEP_INSIGHTS && location.pathname.startsWith(ROUTES.DEEP_INSIGHTS_CHAT);
                }
                if (!showThreads) {
                  const allowNewChat =
                    selectedAgent === RBAC_APPS.DEEP_INSIGHTS ||
                    location.pathname === ROUTES.VIDEO_TUTORIALS ||
                    isTalentPulsePage;
                  return allowNewChat && tab.id === "new-chat";
                }
                return true;
              })
              .map((tab) => {
                const label = tab.id === "apps" ? MY_APPS : tab.id === "new-chat" ? NEW_CHAT : tab.label;
                const isActive = tab.id !== "new-chat" && location.pathname.startsWith(tab.path);
                const src = isActive ? (iconMap[tab.activeIcon] || tab.activeIcon) : (iconMap[tab.icon] || tab.icon);

                const tabEl = (
                  <GenericTooltip
                    key={tab.id}
                    tooltipText={isCollapsed ? label : ""}
                    tooltipPosition="right"
                    className="sidebar-tooltip"
                  >
                    <div
                      className={`nav-item ${isActive ? "active" : ""} nav-item-${tab.id}`}
                      onClick={() => handleTabClick(tab)}
                    >
                      <div className="icon-container">
                        <img src={src} alt={label} />
                      </div>
                      <span className="nav-label">{label}</span>
                      {tab.count !== undefined && (
                        <span className="nav-badge">{tab.count}</span>
                      )}
                    </div>
                  </GenericTooltip>
                );

                return [tabEl];
              })}
          </div>

          {showThreads && (
            <div
              className={`sidebar-threads-container ${!isCollapsed ? "visible" : "hidden"}`}
              style={{ marginBottom: isProfileMenuOpen ? `${dropdownHeight}px` : "0px" }}
            >
              <Threads
                config={activeThreadConfig}
                showSearchBar={showSearchBarInThreads}
                isCollapsedHistory={isHistoryCollapsed}
                setIsCollapsedHistory={setManuallyCollapsedHistory}
                maxPinThreads={activeThreadConfig?.agentType === AGENT_TYPES.DEEP_INSIGHTS ? 5 : undefined}
              />
            </div>
          )}
        </div>

        <div className="sidebar-footer" ref={profileRef}>
          <button
            className={`profile-btn ${isProfileMenuOpen ? 'active' : ''}`}
            onClick={() => {
              setIsCollapsed(false);
              setIsProfileMenuOpen((prev) => !prev);
              setShowUserGuideSubMenu(false);
            }}
            title={fullName}
          >
            <div className="profile-icon">{initials}</div>
            <span className="profile-name">{fullName}</span>
          </button>

          {isProfileMenuOpen && (
            <div className={`sidebar-profile-dropdown ${isCollapsed ? 'collapsed-dropdown' : ''}`} ref={dropdownRef}>
              <div className="dropdown-email">{username}</div>
              {profileMenuItems.map((item) => (
                <React.Fragment key={item.id}>
                  {item.id === 'logout' && <div className="dropdown-divider"></div>}
                  {item.hasSubmenu ? (
                    <div
                      className="dropdown-item user-guide-item"
                      onMouseEnter={() => setShowUserGuideSubMenu(true)}
                      onMouseLeave={() => setShowUserGuideSubMenu(false)}
                    >
                      <div className="dropdown-item-content">
                        <img src={item.icon} alt={item.label} />
                        <span>{item.label}</span>
                      </div>
                      {showUserGuideSubMenu && (
                        <div className="sidebar-submenu-dropdown">
                          {item.submenuItems.map((subItem) => (
                            <div key={subItem.id} className="dropdown-item" onClick={subItem.action}>
                              <img src={subItem.icon} alt={subItem.label} />
                              <span>{subItem.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="dropdown-item" onClick={item.action}>
                      <img src={item.icon} alt={item.label} />
                      <span>{item.label}</span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

          )}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal open={showLogoutModal} onClose={() => setShowLogoutModal(false)}>
        <div className="modal-inner-content">
          <div className="modal-title">{LOGOUT}</div>
          <div className="modal-message">
            {LOGOUT_CONFIRM_MSG}
          </div>
          <div className="modal-buttons">
            <button
              onClick={() => setShowLogoutModal(false)}
              className="button-style primary-grey-button"
            >
              {CANCEL}
            </button>
            <button
              onClick={handleLogout}
              className="button-style primary-button"
            >
              {LOGOUT}
            </button>
          </div>
        </div>
      </Modal>

      <Policy
        open={openPDF}
        onClose={() => {
          setOpenPDF(false);
          emit("policyPopupClosed");
        }}
        handleOnclickSubmit={handlePolicyAccept}
        pdfFile={
          modelTitle === AI_USAGE_POLICY ? aiUsagePolicyPDF :
            modelTitle === USER_GUIDE_DOC ? chatGenieUserGuidePDF :
              modelTitle === FAQS ? faqPDF :
                modelSelectionGuidePDF
        }
        isPolicyFirst={
          modelTitle === AI_USAGE_POLICY
            ? localStorage.getItem(STORAGE_KEYS.IS_POLICY) === "false"
            : modelTitle === PII_POLICY ? !isPersonalPolicyAccepted : false
        }
        heading={modelTitle}
        buttonTitle="Accept & Continue"
      />
    </>
  );
}

export default Sidebar;
